import cuid from "cuid";
import dayjs from "dayjs";
import { get } from "lodash";
import moment from "moment";
import { useState } from "react";
import {
  MdOutlineApartment,
  MdOutlineCake,
  MdOutlineChangeCircle,
  MdOutlineDescription,
  MdOutlineHistory,
  MdOutlineLocationOn,
  MdSocialDistance,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import format from "string-template";
import { DATE_FORMAT, PROJECT_TYPES, SALE_MODES } from "utils/constants";
import {
  bindDateInputProps,
  bindInputProps,
  bindTextFieldInputProps,
} from "utils/helpers/input";
import { filterContractNumber } from "utils/helpers/validations";

export const step = { CONTRACT: "CONTRACT" };

const Contract = ({
  index,
  translation,
  deltaRia,
  deltaStart,
  deltaEnd,
  deltaEndRia,
  retroactivityEnabled,
  values,
  ...formProps
}) => {
  const [pickerValue, setPickerValue] = useState(
    !get(values, "contract.issuanceDate") &&
      dayjs(moment.max(moment(), moment().startOf("y")).toDate()),
  );
  const [showInfo, setShowInfo] = useState(false);
  const checkExpired = ({ issuanceDate, isRia }) => {
    if (values.retroactive) {
      const startOfMonth = dayjs().startOf("month");
      const expired =
        values.expired || dayjs(issuanceDate).isBefore(startOfMonth);
      return expired;
    }
    const minIssuanceDate = dayjs()
      .startOf("day")
      .add(isRia ? deltaRia : deltaStart, "days");
    const expired =
      values.expired || dayjs(issuanceDate).isBefore(minIssuanceDate);

    return expired;
  };

  return (
    <div className="contact-stepper" key={step.CONTRACT}>
      <div className="contact-stepper__header">
        <div className="contact-stepper__header--counter">
          <span>{index}</span>
        </div>
        <div className="contact-stepper__header--content">
          <h1 className="contact-stepper__header--content--title">
            <span>
              {translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].TITLE}
            </span>
          </h1>
          <p className="contact-stepper__header--content--description">
            {translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].DESCRIPTION}
          </p>
        </div>
      </div>
      <div className="contact-stepper__content">
        {checkExpired({
          issuanceDate: values?.contract?.issuanceDate,
          isRia: values.ria,
        }) && (
          <Alert
            showIcon
            type="error"
            message={
              translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].INVALID_DATE
            }
            description={format(
              translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT]
                .INVALID_ISSUANCE,
              {
                date: dayjs()
                  .add(values.ria ? deltaRia : deltaStart, "days")
                  .format(DATE_FORMAT),
              },
            )}
          />
        )}
        <Input
          type="switch"
          icon={<MdOutlineChangeCircle size={22} />}
          label={
            translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.CONTRACT
              .LABEL
          }
          checkedChildren={
            translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.CONTRACT
              .OPTIONS.YES
          }
          unCheckedChildren={
            translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.CONTRACT
              .OPTIONS.NO
          }
          checked={values.ria}
          onChange={(value) => {
            formProps.setFieldValue("ria", value);
            if (value) {
              formProps.setFieldValue("retroactive", false);
              formProps.setFieldValue(
                "terminationRequest.id",
                get(values, "terminationRequest.id") || cuid(),
              );
            } else {
              formProps.setFieldValue("terminationRequest", null);
            }
            if (
              value &&
              checkExpired({
                issuanceDate: values?.contract?.issuanceDate,
                isRia: value,
              })
            ) {
              setShowInfo(true);
              formProps.setFieldValue(
                "contract.issuanceDate",
                dayjs().add(deltaRia, "days").toDate(),
              );
            }
            setPickerValue(
              value
                ? dayjs().add(deltaRia, "days")
                : dayjs().add(1, "y").startOf("y").add(5, "h"),
            );
          }}
        />
        {values.ria && (
          <>
            <Input
              required
              icon={<MdOutlineDescription size={22} />}
              label={
                translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM
                  .CURRENT_CONTRACT.LABEL
              }
              placeholder={
                translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM
                  .CURRENT_CONTRACT.PLACEHOLDER
              }
              trim
              {...bindInputProps({
                name: "terminationRequest.contractNumber",
                values,
                ...formProps,
              })}
              onChange={({ target: { value } }) => {
                formProps.setFieldValue(
                  "terminationRequest.contractNumber",
                  filterContractNumber(value),
                );
              }}
            />
            <Input
              required
              icon={<MdOutlineApartment size={22} />}
              label={
                translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM
                  .CURRENT_COMPANY.LABEL
              }
              placeholder={
                translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM
                  .CURRENT_COMPANY.PLACEHOLDER
              }
              {...bindTextFieldInputProps({
                name: "terminationRequest.brokerageMandate.currentCompany",
                values,
                ...formProps,
              })}
              onChange={({ target: { value } }) => {
                formProps.setFieldValue(
                  "terminationRequest.brokerageMandate.id",
                  get(values, "terminationRequest.brokerageMandate.id") ||
                    cuid(),
                );
                formProps.setFieldValue(
                  "terminationRequest.brokerageMandate.currentCompany",
                  value,
                );
              }}
            />
            <Input
              required
              icon={<MdOutlineLocationOn size={22} />}
              label={
                translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM
                  .CURRENT_COMPANY_ADDRESS.LABEL
              }
              placeholder={
                translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM
                  .CURRENT_COMPANY_ADDRESS.PLACEHOLDER
              }
              {...bindTextFieldInputProps({
                name: "terminationRequest.address",
                values,
                ...formProps,
              })}
            />
          </>
        )}
        <Input
          type="switch"
          icon={<MdOutlineHistory size={22} />}
          label={
            translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.RETROACTIVE
              .LABEL
          }
          checkedChildren={
            translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.RETROACTIVE
              .OPTIONS.YES
          }
          unCheckedChildren={
            translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.RETROACTIVE
              .OPTIONS.NO
          }
          checked={values.retroactive === true}
          onChange={(value) => {
            formProps.setFieldValue("retroactive", value);
          }}
          disabled={values.ria || !retroactivityEnabled}
        />
        <Input
          required
          icon={<MdOutlineCake size={22} />}
          label={
            translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.START_DATE
              .LABEL
          }
          placeholder={
            translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.START_DATE
              .PLACEHOLDER
          }
          type="date"
          variant="simple"
          format={DATE_FORMAT}
          disabledDate={(current) => {
            if (values.retroactive) {
              const startOfMonth = dayjs().startOf("month");
              return current && current < startOfMonth;
            }
            const validStartDate = values.ria
              ? dayjs(
                  moment
                    .max(
                      moment().add(deltaRia, "days").startOf("day"),
                      moment().startOf("y"),
                    )
                    .toDate(),
                )
              : dayjs(
                  moment
                    .max(
                      moment().add(deltaStart, "days").startOf("day"),
                      moment().startOf("y"),
                    )
                    .toDate(),
                );
            return current && current < validStartDate;
          }}
          defaultPickerValue={pickerValue}
          {...bindDateInputProps({
            name: "contract.issuanceDate",
            values,
            ...formProps,
          })}
        />
        <>
          {showInfo && (
            <Alert
              showIcon
              type="info"
              message={format(
                translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT]
                  .INVALID_ISSUANCE,
                {
                  date: dayjs()
                    .add(values.ria ? deltaRia : 0, "days")
                    .format(DATE_FORMAT),
                },
              )}
            />
          )}
        </>
        <Input
          type="switch"
          icon={<MdSocialDistance size={22} />}
          label={
            translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM
              .CONTRACT_TYPE.LABEL
          }
          checkedChildren={
            translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM
              .CONTRACT_TYPE.OPTIONS.YES
          }
          unCheckedChildren={
            translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM
              .CONTRACT_TYPE.OPTIONS.NO
          }
          checked={get(values, "fields.twoTimesSale") === true}
          onChange={(value) => {
            formProps.setFieldValue("fields.twoTimesSale", value);
          }}
        />
        <Select
          required
          icon={<MdSocialDistance size={22} />}
          options={Object.entries(SALE_MODES).map(([key, value]) => ({
            value,
            label:
              translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.SALE_MODE
                .OPTIONS[key],
          }))}
          label={
            translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.SALE_MODE
              .LABEL
          }
          placeholder={
            translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.SALE_MODE
              .PLACEHOLDER
          }
          {...bindInputProps({
            name: "contract.saleMode",
            values,
            ...formProps,
          })}
          onChange={(value) => {
            formProps.setFieldValue("contract.saleMode", value);
            formProps.setFieldValue(
              "projectType",
              value === SALE_MODES.FACE_TO_FACE ? PROJECT_TYPES.HOT_LEAD : null,
            );
          }}
        />
        <Select
          required
          icon={<MdSocialDistance size={22} />}
          options={Object.entries(PROJECT_TYPES).map(([key, value]) => ({
            value,
            label:
              translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM
                .PROJECT_TYPE.OPTIONS[key],
          }))}
          label={
            translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.PROJECT_TYPE
              .LABEL
          }
          placeholder={
            translation.TEMPLATES.CONTACT.STEPS[step.CONTRACT].FORM.PROJECT_TYPE
              .PLACEHOLDER
          }
          disabled={
            get(values, "contract.saleMode") === SALE_MODES.FACE_TO_FACE
          }
          {...bindInputProps({
            name: "projectType",
            values,
            ...formProps,
          })}
          onChange={(value) => {
            formProps.setFieldValue("projectType", value);
          }}
        />
      </div>
    </div>
  );
};
export default Contract;
